import React, { useState, useEffect, useMemo } from "react"
import { Container, Button, Modal, ModalHeader, ModalBody, ModalFooter, Label, Input } from "reactstrap"
import { withTranslation } from "react-i18next"
import PropTypes from "prop-types"
import { getAllExpenses, generateExpenseRefundCode, refundExpense, createOfficeExpense } from "../../api"
import TableContainer from "../../components/Common/TableContainer"

function OfficeExpensesPage({ t }) {
  document.title = t("Office Expenses")

  const [expenses, setExpenses] = useState([])
  const [filteredExpenses, setFilteredExpenses] = useState([])
  const [loadingExpenses, setLoadingExpenses] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")

  // Create expense modal
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [amount, setAmount] = useState("")
  const [description, setDescription] = useState("")
  const [balanceType, setBalanceType] = useState("fiat")
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  // Refund modal
  const [showRefundModal, setShowRefundModal] = useState(false)
  const [selectedExpense, setSelectedExpense] = useState(null)
  const [refundCode, setRefundCode] = useState("")
  const [refunding, setRefunding] = useState(false)
  const [refundError, setRefundError] = useState("")

  useEffect(() => {
    loadExpenses()
  }, [])

  // Filter expenses when search term changes
  useEffect(() => {
    if (!searchTerm) {
      setFilteredExpenses(expenses)
      return
    }

    const searchLower = searchTerm.toLowerCase()
    const filtered = expenses.filter(expense => {
      return (
        (expense.expenseNumber && expense.expenseNumber.toString().toLowerCase().includes(searchLower)) ||
        (expense.description && expense.description.toLowerCase().includes(searchLower)) ||
        (expense.balanceType && expense.balanceType.toLowerCase().includes(searchLower)) ||
        (expense.amount && expense.amount.toString().toLowerCase().includes(searchLower))
      )
    })
    setFilteredExpenses(filtered)
  }, [searchTerm, expenses])

  const loadExpenses = () => {
    setLoadingExpenses(true)
    getAllExpenses()
      .then((data) => {
        setExpenses(data)
        setFilteredExpenses(data)
      })
      .catch((error) => {
        console.error("Error loading expenses:", error)
      })
      .finally(() => {
        setLoadingExpenses(false)
      })
  }

  const handleCreateClick = () => {
    setAmount("")
    setDescription("")
    setBalanceType("fiat")
    setShowCreateModal(true)
  }

  const handleCreateSubmit = () => {
    if (!amount || parseFloat(amount) <= 0) {
      alert(t("Please enter a valid amount"))
      return
    }
    if (!description || description.trim() === "") {
      alert(t("Please enter a description"))
      return
    }

    setShowCreateModal(false)
    setShowConfirmModal(true)
  }

  const handleConfirmCreate = () => {
    setSubmitting(true)
    setShowConfirmModal(false)

    createOfficeExpense({
      amount: parseFloat(amount),
      description,
      balanceType
    })
      .then(() => {
        alert(t("Expense recorded successfully!"))
        setAmount("")
        setDescription("")
        setBalanceType("fiat")
        loadExpenses()
      })
      .catch((error) => {
        alert(t("Failed to record expense: ") + error.message)
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  const handleRefundClick = (expense) => {
    setSelectedExpense(expense)
    setRefundCode("")
    setRefundError("")

    generateExpenseRefundCode(expense.expenseNumber)
      .then(() => {
        setShowRefundModal(true)
      })
      .catch((error) => {
        alert(t("Failed to generate refund code. Please try again."))
        console.error("Error generating refund code:", error)
      })
  }

  const handleRefundConfirm = () => {
    if (!refundCode || refundCode.trim() === "") {
      setRefundError(t("Please enter the code"))
      return
    }

    setRefunding(true)
    setRefundError("")

    refundExpense(selectedExpense.expenseNumber, refundCode)
      .then(() => {
        setShowRefundModal(false)
        alert(t("Expense refunded successfully!"))
        loadExpenses()
      })
      .catch((error) => {
        setRefundError(t("Invalid code or refund failed. Please try again."))
        console.error("Error refunding expense:", error)
      })
      .finally(() => {
        setRefunding(false)
      })
  }

  const formatDate = (timestamp) => {
    if (!timestamp) return ""
    const date = new Date(timestamp)
    return date.toLocaleDateString() + " " + date.toLocaleTimeString()
  }

  const columns = useMemo(
    () => [
      {
        header: t("Number"),
        accessorKey: "expenseNumber",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: t("Date"),
        accessorKey: "date",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ cell }) => formatDate(cell.getValue()),
      },
      {
        header: t("Description"),
        accessorKey: "description",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ cell, row }) => {
          const isRefunded = row.original.wasRefunded
          return (
            <span style={{ textDecoration: isRefunded ? "line-through" : "none" }}>
              {cell.getValue()}
            </span>
          )
        },
      },
      {
        header: t("Amount"),
        accessorKey: "amount",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ cell, row }) => {
          const isRefunded = row.original.wasRefunded
          return (
            <span style={{ textDecoration: isRefunded ? "line-through" : "none" }}>
              ${cell.getValue()}
            </span>
          )
        },
      },
      {
        header: t("Balance Type"),
        accessorKey: "balanceType",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ cell }) => {
          const balanceType = cell.getValue()
          const bgColor = balanceType === "crypto" ? "#17a2b8" : "#007bff"
          return (
            <span
              style={{
                backgroundColor: bgColor,
                color: "white",
                padding: "2px 8px",
                borderRadius: "3px",
                fontSize: "0.85em",
              }}
            >
              {balanceType === "crypto" ? t("Crypto") : t("Fiat")}
            </span>
          )
        },
      },
      {
        header: t("Status"),
        accessorKey: "wasRefunded",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ cell }) => {
          const wasRefunded = cell.getValue()
          const bgColor = wasRefunded ? "#6c757d" : "#28a745"
          return (
            <span
              style={{
                backgroundColor: bgColor,
                color: "white",
                padding: "0.2em 0.5em",
                borderRadius: "4px",
              }}
            >
              {wasRefunded ? t("Refunded") : t("Active")}
            </span>
          )
        },
      },
      {
        header: t("Actions"),
        id: "actions",
        cell: ({ row }) => {
          const expense = row.original
          return !expense.wasRefunded ? (
            <button
              className="btn btn-sm btn-warning"
              onClick={() => handleRefundClick(expense)}
            >
              {t("Refund")}
            </button>
          ) : (
            <span></span>
          )
        },
      },
    ],
    [t]
  )

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <h2>{t("Office Expenses")}</h2>
          <div className="mb-3 d-flex gap-2" style={{ alignItems: "flex-start" }}>
            <input
              type="text"
              className="form-control search-box"
              placeholder={`${t("Search by description, amount, or any field")}... ${expenses.length} ${t("records")}`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ maxWidth: '500px' }}
            />
            <Button
              color="primary"
              onClick={handleCreateClick}
            >
              <i className="bx bx-plus me-1"></i>
              {t("Add Expense")}
            </Button>
          </div>
          {searchTerm && (
            <small className="text-muted d-block mb-2">
              {t("Showing")} {filteredExpenses.length} {t("of")} {expenses.length} {t("expenses")}
            </small>
          )}

          {loadingExpenses ? (
            <p>{t("Loading...")}</p>
          ) : (
            <TableContainer
              columns={columns}
              data={filteredExpenses || []}
              isGlobalFilter={false}
              isCustomPageSize={true}
              isPagination={true}
              pagination="pagination"
              paginationWrapper="dataTables_paginate paging_simple_numbers"
              tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
            />
          )}
        </Container>

        {/* Create Expense Modal */}
        <Modal isOpen={showCreateModal} toggle={() => setShowCreateModal(false)}>
          <ModalHeader toggle={() => setShowCreateModal(false)}>
            {t("Add Expense")}
          </ModalHeader>
          <ModalBody>
            <div className="mb-3">
              <Label htmlFor="amount">{t("Amount")} (USD)</Label>
              <Input
                type="number"
                id="amount"
                className="form-control"
                placeholder={t("Enter amount")}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                step="0.01"
                min="0.01"
              />
            </div>

            <div className="mb-3">
              <Label htmlFor="description">{t("Description")}</Label>
              <Input
                type="textarea"
                id="description"
                className="form-control"
                placeholder={t("e.g. Rent, Salary, Utilities, Office supplies")}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows="3"
              />
            </div>

            <div className="mb-3">
              <Label>{t("Deduct from Balance")}</Label>
              <div>
                <div className="form-check form-check-inline">
                  <Input
                    type="radio"
                    id="balanceFiat"
                    name="balanceType"
                    className="form-check-input"
                    checked={balanceType === "fiat"}
                    onChange={() => setBalanceType("fiat")}
                  />
                  <Label className="form-check-label" htmlFor="balanceFiat">
                    {t("Fiat Balance")}
                  </Label>
                </div>
                <div className="form-check form-check-inline">
                  <Input
                    type="radio"
                    id="balanceCrypto"
                    name="balanceType"
                    className="form-check-input"
                    checked={balanceType === "crypto"}
                    onChange={() => setBalanceType("crypto")}
                  />
                  <Label className="form-check-label" htmlFor="balanceCrypto">
                    {t("Crypto Balance")}
                  </Label>
                </div>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setShowCreateModal(false)}>
              {t("Cancel")}
            </Button>
            <Button color="primary" onClick={handleCreateSubmit}>
              {t("Continue")}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Confirmation Modal */}
        <Modal isOpen={showConfirmModal} toggle={() => setShowConfirmModal(false)}>
          <ModalHeader toggle={() => setShowConfirmModal(false)}>
            {t("Confirm Expense")}
          </ModalHeader>
          <ModalBody>
            <p>{t("Please confirm the following expense:")}</p>
            <table className="table table-borderless">
              <tbody>
                <tr>
                  <td><strong>{t("Amount")}:</strong></td>
                  <td>${amount}</td>
                </tr>
                <tr>
                  <td><strong>{t("Description")}:</strong></td>
                  <td>{description}</td>
                </tr>
                <tr>
                  <td><strong>{t("Deduct from")}:</strong></td>
                  <td>{balanceType === "fiat" ? t("Fiat Balance") : t("Crypto Balance")}</td>
                </tr>
              </tbody>
            </table>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setShowConfirmModal(false)}>
              {t("Cancel")}
            </Button>
            <Button color="primary" onClick={handleConfirmCreate} disabled={submitting}>
              {submitting ? t("Submitting...") : t("Confirm")}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Refund Modal */}
        <Modal isOpen={showRefundModal} toggle={() => setShowRefundModal(false)}>
          <ModalHeader toggle={() => setShowRefundModal(false)}>
            {t("Refund Expense - 2FA Required")}
          </ModalHeader>
          <ModalBody>
            <p>{t("A refund code has been sent to your Telegram. Please enter the code to confirm the refund.")}</p>
            <p className="text-warning">
              <strong>{t("Warning")}:</strong> {t("This will return the amount to the balance.")}
            </p>
            {selectedExpense && (
              <div className="mb-3">
                <p><strong>{t("Expense")} #{selectedExpense.expenseNumber}</strong></p>
                <p>{t("Amount")}: ${selectedExpense.amount}</p>
                <p>{t("Description")}: {selectedExpense.description}</p>
              </div>
            )}
            <div className="mb-3">
              <Label htmlFor="refundCode">{t("Enter Refund Code")}</Label>
              <Input
                type="text"
                id="refundCode"
                className="form-control"
                placeholder={t("Enter 5-digit code")}
                value={refundCode}
                onChange={(e) => setRefundCode(e.target.value)}
                maxLength={5}
              />
            </div>
            {refundError && (
              <div className="alert alert-danger">{refundError}</div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setShowRefundModal(false)} disabled={refunding}>
              {t("Cancel")}
            </Button>
            <Button color="warning" onClick={handleRefundConfirm} disabled={refunding}>
              {refunding ? t("Refunding...") : t("Confirm Refund")}
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </React.Fragment>
  )
}

OfficeExpensesPage.propTypes = {
  t: PropTypes.func.isRequired,
}

export default withTranslation()(OfficeExpensesPage)
